import { Badge } from '@atoms/Badges';
import { NumberInput } from '@atoms/Forms/Inputs';
import { Bold } from '@atoms/Typographies';
import { Constant } from '@configs/index';
import React, { ChangeEvent } from 'react';
import { Table as BootstrapTable } from 'react-bootstrap';
import { ArrowDownShort, ArrowsExpand, ArrowUpShort } from 'react-bootstrap-icons';
import Select from 'react-select';
import { usePagination, useSortBy, useTable } from 'react-table';
import styled from 'styled-components';

type ColumnProps = {
    Header: string;
    accessor: string;
    typeOption?: any;
    disableSortBy?: boolean;
    Cell?: object;
};

type Props = {
    pageSize?: number;
    pageIndex?: number;
    column: ColumnProps[];
    data: any;
};

const selectPageSize = Constant.PAGE_SIZE;

const ListTable = (props: Props) => {
    const columns: any = props.column;
    const data = props.data;
    const dataCount = data.length;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable({ columns, data, initialState: { pageIndex: props.pageIndex ?? 0, pageSize: props.pageSize ?? 10 } }, useSortBy, usePagination);

    return (
        <ListTableWithStyled>
            <TableHeaderWithStyled className="mb-2 d-flex gap-2 justify-content-end">
                <PageSizeWithStyled>
                    <Select options={selectPageSize} defaultValue={selectPageSize[3]} onChange={e => { setPageSize(Number(e?.value)) }} />
                </PageSizeWithStyled>
                <PageJumpWithStyled className="d-flex align-items-center mb-1 text-nowrap">
                    <NumberInput defaultValue={pageIndex + 1} min={1} max={pageCount} onChange={(e: ChangeEvent<HTMLInputElement>) => { const page = e.target.value ? Number(e.target.value) - 1 : 0; gotoPage(page); }} />&nbsp;
                    <PageJumpTextWithStyled>ページへ</PageJumpTextWithStyled>
                </PageJumpWithStyled>
            </TableHeaderWithStyled>

            <TableWrapperWithStyled className="table-wrapper">
                <BootstrapTable className="text-center table-light" striped bordered {...getTableProps()}>
                    <THead className="table-primary">
                        {headerGroups.map(headerGroup => (
                            <TR {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <TH {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}>
                                        {column.render("Header")}
                                        {column.disableSortBy ? null :
                                            (
                                                <span>
                                                    &nbsp;
                                                    <TableSortWithStyled>
                                                        {column.isSorted ? column.isSortedDesc ? <ArrowUpShort /> : <ArrowDownShort /> : <ArrowsExpand />}
                                                    </TableSortWithStyled>
                                                </span>
                                            )}
                                    </TH>
                                ))}
                            </TR>
                        ))}
                    </THead>
                    <TBody {...getTableBodyProps()}>
                        {page.length ?
                            page.map((row: any) => {
                                prepareRow(row);
                                return (
                                    <TR {...row.getRowProps()}>
                                        {row.cells.map((cell: any) => {
                                            const { type, typeOption } = cell?.column;

                                            switch (type) {
                                                case 'badge':
                                                    let badgeClass;
                                                    typeOption.forEach((element: any) => {
                                                        switch (cell.value) {
                                                            case element.status_name:
                                                                badgeClass = element.color_class;
                                                                break;
                                                        }
                                                    });

                                                    return (
                                                        <TD {...cell.getCellProps()}>
                                                            <Badge badgeType={badgeClass} badgeValue={cell.render('Cell')} />
                                                        </TD>
                                                    );
                                                default:
                                                    return <TD {...cell.getCellProps()}>{cell.render('Cell')}</TD>
                                            }
                                        })}
                                    </TR>
                                );
                            })
                            : <TR><ErrorTD colSpan={columns.length}>データがありません</ErrorTD></TR>}
                    </TBody>
                </BootstrapTable>
            </TableWrapperWithStyled>

            <TableFooterWithStyled className="text-end">
                <NumberInfoWithStyled>
                    全<Bold>{dataCount}</Bold>件中
                    {page.length ? (pageIndex * pageSize) + 1 : 0} 件 -
                    {(pageIndex * pageSize) + page.length}件を表示
                </NumberInfoWithStyled>
            </TableFooterWithStyled>

            {page.length ?
                <PageNavigationWithStyled aria-label="Page navigation">
                    <UL className="pagination justify-content-center mb-2">
                        <LI className="page-item"><button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>&laquo;</button></LI>
                        <LI className="page-item"><button className="page-link" onClick={() => previousPage()} disabled={!canPreviousPage}>前へ</button></LI>
                        <LI className="page-item active"><span className="page-link">{pageIndex + 1}</span></LI>
                        <LI className="page-item"><button className="page-link" onClick={() => nextPage()} disabled={!canNextPage}>次へ</button></LI>
                        <LI className="page-item"><button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>&raquo;</button></LI>
                    </UL>
                    <PaginationInfoWithStyled className="text-center">
                        <AllPageNumberWithStyled className="text-secondary">（全&nbsp;<Bold>{pageCount}</Bold>&nbsp;ページ）</AllPageNumberWithStyled>
                    </PaginationInfoWithStyled>
                </PageNavigationWithStyled>
                : <NoPageNavigationWithStyled></NoPageNavigationWithStyled>
            }
        </ListTableWithStyled>
    );
}

const ListTableWithStyled = styled.div`
    & thead {
        border-bottom-width: 2px;
    }
`;
const TableWrapperWithStyled = styled.div`
    overflow-x: auto;
`;
const TableHeaderWithStyled = styled.div`
    display: none !important;
`;
const THead = styled.thead`
`;
const TBody = styled.tbody`
`;
const TR = styled.tr`
`;
const TH = styled.th`
`;
const TD = styled.td`
`
const ErrorTD = styled.td`
    padding: 10px !important;
`
const TableSortWithStyled = styled.span`
`
const PageSizeWithStyled = styled.div`
    font-size: 14px;
`;
const TableFooterWithStyled = styled.div`
`;
const PageJumpWithStyled = styled.div`
    width: 150px;
`;
const PageJumpTextWithStyled = styled.small`
`;
const NumberInfoWithStyled = styled.small`
`;
const PageNavigationWithStyled = styled.nav`
`;
const NoPageNavigationWithStyled = styled.nav`
    padding: 5px;
`;
const UL = styled.ul`
`;
const LI = styled.li`
`;
const PaginationInfoWithStyled = styled.div`
`;
const AllPageNumberWithStyled = styled.small`
`;

export default ListTable;